import { FC, useMemo, useState } from 'react';
import { useModal } from '@/hooks';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  ActivateRiskProfileRequest,
  BaseRiskProfileRequest,
  RiskProfile,
} from '@/api';
import { riskscoreProfilesActions } from '@/redux/actions';
import {
  Button,
  ConfirmActionModal,
  FieldInfoGroup,
  RiskscoreCreateProfileModal,
  RiskscoreProfileDetailsModal,
  RiskscoreProfileModal,
  SectionBox,
} from '@/components';

import styles from './RiskscoreSettings.module.sass';

interface RiskscoreSettingsProps {}

const RiskscoreSettings: FC<RiskscoreSettingsProps> = () => {
  const { t } = useTranslation();
  const [activeProfileId, setActiveProfileId] = useState<number | null>(null);
  const [profileForDelete, setProfileForDelete] = useState<RiskProfile | null>(
    null,
  );
  const dispatch = useDispatch();

  const {
    isOpen: isOpenProfileModal,
    onOpenModal: handleOpenProfileModal,
    onCloseModal: handleCloseProfileModal,
  } = useModal();

  const {
    isOpen: isOpenCreateProfileModal,
    onOpenModal: handleOpenCreateProfileModal,
    onCloseModal: handleCloseCreateProfileModal,
  } = useModal();

  const {
    isOpen: isOpenProfileDetailsModal,
    onOpenModal: handleOpenProfileDetailsModal,
    onCloseModal: handleCloseProfileDetailsModal,
  } = useModal();

  const {
    isOpen: isOpenConfirmModal,
    onOpenModal: handleOpenConfirmModal,
    onCloseModal: handleCloseConfirmModal,
  } = useModal();

  const onActivate = (profileId: number) => {
    if (profileId) {
      const payload: ActivateRiskProfileRequest = { profileId };
      dispatch(riskscoreProfilesActions.activateRiskProfile(payload));
    }
  };

  const onDetails = (profileId: number) => {
    setActiveProfileId(() => profileId);
    handleOpenProfileDetailsModal();
  };

  const onEdit = (profile: number) => {
    // if (profile) {
    // setProfileForDelete(() => profile);
    // handleOpenConfirmModal();
    // }
  };

  const onDelete = (profile: RiskProfile) => {
    if (profile) {
      setProfileForDelete(() => profile);
      handleOpenConfirmModal();
    }
  };

  const triggerDelete = () => {
    if (profileForDelete) {
      const profileId = profileForDelete.id;
      const payload: BaseRiskProfileRequest = { profileId };
      dispatch(riskscoreProfilesActions.deleteRiskProfile(payload));
      handleCloseConfirmModal();
    }
  };

  const confirmDeleteMessage = useMemo(() => {
    return parse(
      t('translation.confirm_risk_profile_delete', {
        profileName: profileForDelete?.name,
      }),
    );
  }, [profileForDelete]);

  const riskProfileSectionTitle = t('translation.riskscore_modal_title');

  return (
    <SectionBox>
      {{
        body: (
          <>
            <FieldInfoGroup
              title={riskProfileSectionTitle}
              actions={
                <Button
                  type="secondary"
                  corner="rect"
                  className={styles.actionButton}
                  onClick={handleOpenProfileModal}
                >
                  {t('translation.edit_risk_profile_link')}
                </Button>
              }
            />

            <RiskscoreProfileModal
              isOpen={isOpenProfileModal}
              onClose={handleCloseProfileModal}
              onCreateProfile={() => handleOpenCreateProfileModal()}
              onActivate={(profileId) => onActivate(profileId)}
              onDetails={(profileId) => onDetails(profileId)}
              onDelete={(profile) => onDelete(profile)}
            />

            <RiskscoreCreateProfileModal
              isOpen={isOpenCreateProfileModal}
              onClose={handleCloseCreateProfileModal}
            />

            <RiskscoreProfileDetailsModal
              isOpen={isOpenProfileDetailsModal}
              onClose={handleCloseProfileDetailsModal}
              profileId={activeProfileId}
              onEdit={(profileId) => onEdit(profileId)}
            />

            <ConfirmActionModal
              title={t('translation.alert_warning_title')}
              isOpen={isOpenConfirmModal}
              onClose={handleCloseConfirmModal}
              onConfirm={triggerDelete}
              confirmButtonText={t('translation.confirm')}
              cancelButtonText={t('translation.cancel_btn')}
            >
              <span>{confirmDeleteMessage}</span>
            </ConfirmActionModal>
          </>
        ),
      }}
    </SectionBox>
  );
};

export default RiskscoreSettings;
